import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { postApiWithAuth } from "../../services/api/api";
import "./ProfileDashboard.css";

// Import the modular tab components
import DashboardTab from "./tabs/DashboardTab";
import ProfileTab from "./tabs/ProfileTab";
import PlanTab from "./tabs/PlanTab";
import NotificationTab from "./tabs/NotificationTab";
import ResetPasswordTab from "./tabs/ResetPasswordTab";

const ProfileDashboard = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("dashboard");
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // Security check context
    const token = localStorage.getItem("token");
    const isAuthenticated = !!token && token !== "undefined" && token !== "null";

    // Shared layout state variables - Initial fallback updated to local asset
    const [profilePhoto, setProfilePhoto] = useState("assets/imgs/default_user.jpeg");
    const [userProfileData, setUserProfileData] = useState(null);

    // Route Guard Effect
    useEffect(() => {
        if (!isAuthenticated) {
            navigate("/", { replace: true });
        }
    }, [isAuthenticated, navigate]);

    // Initial Profile local shell hydration path
    useEffect(() => {
        if (!isAuthenticated) return;

        const storedProfileStr = localStorage.getItem("profile_data");
        if (storedProfileStr) {
            try {
                const parsedArray = JSON.parse(storedProfileStr);
                const profile = Array.isArray(parsedArray) ? parsedArray[0] : parsedArray;

                if (profile) {
                    setUserProfileData(profile);
                    if (profile.main_photo) {
                        setProfilePhoto(profile.main_photo);
                    }
                }
            } catch (e) {
                console.error("Error parsing profile photo records from local session:", e);
            }
        }
    }, [isAuthenticated]);

    // Logout Handler logic
    const handleLogout = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (isLoggingOut) return;
        setIsLoggingOut(true);

        try {
            await postApiWithAuth("/logout", {});
        } catch (error) {
            console.error("API logout encountered an issue, proceeding with local cleanup.");
        } finally {
            localStorage.clear();
            sessionStorage.clear();
            setIsLoggingOut(false);
            navigate("/", { replace: true });
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    // Dynamic View Router Switcher
    const renderActiveTabContent = () => {
        switch (activeTab) {
            case "dashboard":
                return <DashboardTab userProfileData={userProfileData} />;
            case "profile":
                return <ProfileTab initialProfileData={userProfileData} fallbackPhoto={profilePhoto} navigate={navigate} />;
            case "plan":
                return <PlanTab />;
            case "notification":
                return <NotificationTab />;
            case "resetpassword":
                return <ResetPasswordTab />;
            default:
                return <DashboardTab userProfileData={userProfileData} />;
        }
    };

    return (
        <main className="main_wrapper">
            <section className="dashboard-section">
                <div className="container">
                    <div className="row">
                        {/* Sidebar Navigation Panel */}
                        <div className="sidebar-container col-lg-3">
                            <div className="sidebar-card">
                                <div className="position-relative overflow-hidden rounded-3 mb-3">
                                    <img
                                    src={profilePhoto}
                                    alt="Profile Avatar"
                                    className="profile-img m-o"
                                    onError={(e) => { e.target.src = "assets/imgs/default_user.jpeg"; }}
                                />
                                
                                </div>

                                <ul className="nav flex-column dashboard-tabs" id="v-pills-tab" role="tablist">
                                    <button
                                        type="button"
                                        className={`nav-link ${activeTab === "dashboard" ? "active" : ""}`}
                                        onClick={() => setActiveTab("dashboard")}
                                    >
                                        <i className="fa-solid fa-gauge me-2"></i> Dashboard
                                    </button>

                                    <button
                                        type="button"
                                        className={`nav-link ${activeTab === "profile" ? "active" : ""}`}
                                        onClick={() => setActiveTab("profile")}
                                    >
                                        <i className="fa-regular fa-user me-2"></i> Profile
                                    </button>

                                    {/* <button
                                        type="button"
                                        className={`nav-link ${activeTab === "plan" ? "active" : ""}`}
                                        onClick={() => setActiveTab("plan")}
                                    >
                                        <i className="fa-regular fa-rectangle-list me-2"></i> Plan
                                    </button> */}

                                    <button
                                        type="button"
                                        className={`nav-link ${activeTab === "notification" ? "active" : ""}`}
                                        onClick={() => setActiveTab("notification")}
                                    >
                                        <i className="fa-regular fa-bell me-2"></i> Notification
                                    </button>

                                    <button
                                        type="button"
                                        className={`nav-link ${activeTab === "resetpassword" ? "active" : ""}`}
                                        onClick={() => setActiveTab("resetpassword")}
                                    >
                                        <i className="fa-solid fa-lock me-2"></i> Change Password
                                    </button>

                                    <button
                                        type="button"
                                        className="nav-link"
                                        onClick={handleLogout}
                                        disabled={isLoggingOut}
                                        style={{ cursor: isLoggingOut ? "not-allowed" : "pointer" }}
                                    >
                                        <i className={`fa-solid ${isLoggingOut ? "fa-spinner fa-spin" : "fa-arrow-right-from-bracket"} me-2`} ></i>
                                        {isLoggingOut ? "Logging out..." : "Logout"}
                                    </button>
                                </ul>
                            </div>
                        </div>

                        {/* Content Presentation Panel */}
                        <div className="col-lg-9">
                            {renderActiveTabContent()}
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default ProfileDashboard;